'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getBriefingFreshness } from '@/lib/briefing-freshness';
import { MarketBriefingCard } from './market-briefing-card';
import {
  BriefingPeriod,
  BRIEFING_PERIODS,
  getCurrentPeriodStatus,
  MarketBriefingPeriod
} from './market-briefing-types';

interface MarketBriefingSectionProps {
  onPeriodChange?: (period: BriefingPeriod) => void;
  audioUrls?: { audioUrl1?: string; audioUrl2?: string; audioUrl3?: string };
  /** When the underlying briefing batch was last generated (drives the freshness indicator). */
  briefingGeneratedAt?: Date | { toDate: () => Date } | number | null;
}

export function MarketBriefingSection({ onPeriodChange, audioUrls, briefingGeneratedAt }: MarketBriefingSectionProps) {
  const [activePeriod, setActivePeriod] = useState<BriefingPeriod>('morning');
  const [periods, setPeriods] = useState<MarketBriefingPeriod[]>(BRIEFING_PERIODS);
  const [currentDate, setCurrentDate] = useState<string>('');
  const [activePlayerId, setActivePlayerId] = useState<BriefingPeriod | null>(null);

  // Map period to corresponding audio URL
  const getAudioUrlForPeriod = (periodId: BriefingPeriod): string | undefined => {
    switch (periodId) {
      case 'morning': return audioUrls?.audioUrl1;
      case 'midday': return audioUrls?.audioUrl2;
      case 'evening': return audioUrls?.audioUrl3;
      default: return undefined;
    }
  };

  // Handle play state change - ensure only one player is active
  const handlePlayStateChange = (periodId: BriefingPeriod, isPlaying: boolean) => {
    setActivePlayerId(isPlaying ? periodId : null);
  };

  // Update period statuses based on current time
  useEffect(() => {
    const updateStatuses = () => {
      const statusMap = getCurrentPeriodStatus();
      const updatedPeriods = BRIEFING_PERIODS.map(period => ({
        ...period,
        status: statusMap.get(period.id) || period.status
      }));
      setPeriods(updatedPeriods);

      // Auto-select the current period if there is one
      const currentPeriod = updatedPeriods.find(p => p.status === 'current');
      if (currentPeriod) {
        setActivePeriod(currentPeriod.id);
        onPeriodChange?.(currentPeriod.id);
      }
    };

    // Format current date
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    };
    setCurrentDate(now.toLocaleDateString('en-US', options));

    updateStatuses();

    // Update every minute
    const interval = setInterval(updateStatuses, 60000);
    return () => clearInterval(interval);
  }, [onPeriodChange]);

  const handlePeriodClick = (periodId: BriefingPeriod) => {
    setActivePeriod(periodId);
    onPeriodChange?.(periodId);
  };

  // Derive the muted "updated Xh ago" label + staleness flag from the batch timestamp.
  const freshness = getBriefingFreshness(briefingGeneratedAt);

  return (
    <section className="py-3 md:py-6 px-4 md:px-6 border-b border-slate-700/30 bg-gradient-to-b from-[#0d1117] to-transparent">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4 md:mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-reading-2xl font-bold text-amber-400">
            Market Briefings
          </h2>
          <div className="h-4 w-px bg-slate-700/50 hidden sm:block" />
          <span className="section-label hidden sm:inline">Daily</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-reading-xs text-[#6e7681] font-mono">{currentDate}</span>
          {freshness.label && (
            <>
              <span className="text-[#6e7681] hidden sm:inline" aria-hidden="true">·</span>
              <span className="text-reading-xs text-[#6e7681] font-mono">{freshness.label}</span>
              {freshness.isStale && (
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide',
                    'bg-amber-400/15 text-amber-400 border border-amber-400/20'
                  )}
                >
                  STALE
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {periods.map((period) => {
          const periodAudioUrl = getAudioUrlForPeriod(period.id);
          return (
            <MarketBriefingCard
              key={period.id}
              period={period}
              isActive={activePeriod === period.id}
              onClick={() => handlePeriodClick(period.id)}
              audioSrc={periodAudioUrl}
              isPlayerEnabled={!!periodAudioUrl}
              isPlaying={activePlayerId === period.id}
              onPlayStateChange={(playing) => handlePlayStateChange(period.id, playing)}
            />
          );
        })}
      </div>
    </section>
  );
}
